/**
 * CustomAttributionWindowsPage — FS-249 Wave 6 dash.
 *
 * Trackier-style operator surface for tenant attribution-window configuration.
 * The backend exposes a singleton per-tenant window (click/view lookback days
 * + preset label), plus a catalogue of named presets. This page renders:
 *
 *   1. Topbar: "Attribution Windows" title + Create Window CTA (opens drawer).
 *   2. Filter row: preset-type dropdown (all/click/impression/post-view).
 *   3. Main table: one row per preset (name, type, window-hours,
 *      click-lookback, view-lookback, applied-to) with loading/empty/error
 *      states. The row matching the tenant's current preset renders an
 *      "Applied" pill; all others show "Available" with an Apply button.
 *   4. Create drawer: right-hand sheet with zod + react-hook-form validation
 *      (name, type, window-hours, click-lookback, view-lookback). Submit
 *      POSTs explicit click/view days which the backend stores with
 *      preset='custom'.
 *
 * Design contract: GREEN accent only (green-600/700/50), white / zinc-200 /
 * zinc-900, Inter font, 6-8px radii, inline SVG icons. No emoji, no dark mode,
 * no competitor names. Raw fetch() lives only in ./api (rule D6).
 */
import {
	useCallback,
	useEffect,
	useMemo,
	useState,
	type ReactNode,
} from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import type { Preset } from "./api";
import {
	useCustomAttributionWindowsCreate,
	useCustomAttributionWindowsDelete,
	useCustomAttributionWindowsList,
	useCustomAttributionWindowsPresets,
} from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens — green-only, light theme.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_2px_0_rgb(24_24_27/0.04)]";
const LABEL = "text-[11px] font-semibold uppercase tracking-wider text-zinc-500";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 placeholder:text-zinc-400 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20 disabled:cursor-not-allowed disabled:bg-zinc-50 disabled:text-zinc-500";
const SELECT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN_PRIMARY =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-green-700 bg-green-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"inline-flex items-center gap-1.5 rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_LINK =
	"inline-flex items-center gap-1 rounded-[6px] px-2.5 py-1 text-xs font-semibold text-green-700 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-60";

// ---------------------------------------------------------------------------
// Window-type classification (UI-only — derived from lookback shape).
// Backend stores days; we classify the row as click / post-view / impression
// for filtering purposes only.
// ---------------------------------------------------------------------------

type WindowType = "click" | "post-view" | "impression";
type FilterType = "all" | WindowType;

function classifyPreset(p: Pick<Preset, "click_window_days" | "view_window_days">): WindowType {
	if (p.view_window_days > 0 && p.click_window_days === 0) return "impression";
	if (p.view_window_days > 0 && p.click_window_days > 0) return "post-view";
	return "click";
}

function daysToHours(d: number): number {
	return d * 24;
}

function formatDays(d: number): string {
	if (d === 0) return "Off";
	if (d === 1) return "1 day";
	return `${d} days`;
}

function formatPresetLabel(name: string): string {
	if (!name) return "Default";
	return name.charAt(0).toUpperCase() + name.slice(1);
}

// ---------------------------------------------------------------------------
// Zod schema — create/upsert attribution window.
// ---------------------------------------------------------------------------

const windowTypeValues = ["click", "post-view", "impression"] as const;

const createWindowSchema = z
	.object({
		name: z
			.string()
			.trim()
			.min(2, "Name is required (min 2 chars)")
			.max(64, "Name too long (max 64 chars)"),
		type: z.enum(windowTypeValues),
		window_hours: z
			.number({ message: "Window hours is required" })
			.int("Must be a whole number")
			.min(1, "Must be at least 1 hour")
			.max(8760, "Must be ≤ 8760 hours (1 year)"),
		click_lookback_days: z
			.number({ message: "Click lookback is required" })
			.int("Must be a whole number")
			.min(0, "Must be ≥ 0")
			.max(365, "Must be ≤ 365"),
		view_lookback_days: z
			.number({ message: "View lookback is required" })
			.int("Must be a whole number")
			.min(0, "Must be ≥ 0")
			.max(7, "Must be ≤ 7"),
	})
	.refine((v) => v.click_lookback_days >= 1 || v.view_lookback_days >= 1, {
		message: "At least one lookback must be ≥ 1 day",
		path: ["click_lookback_days"],
	});

type CreateWindowInput = z.infer<typeof createWindowSchema>;

// ---------------------------------------------------------------------------
// Inline SVG icons (no emoji, no icon lib).
// ---------------------------------------------------------------------------

function IconPlus() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M10 4v12M4 10h12" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			className="h-3.5 w-3.5"
			fill="none"
			stroke="currentColor"
			strokeWidth={2.4}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M4 10.5 8.5 15 16 6" />
		</svg>
	);
}

function IconClose() {
	return (
		<svg
			aria-hidden="true"
			viewBox="0 0 20 20"
			className="h-4 w-4"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
		>
			<path d="M5 5l10 10M15 5L5 15" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function CustomAttributionWindowsPage() {
	const listQuery = useCustomAttributionWindowsList();
	const presetsQuery = useCustomAttributionWindowsPresets();
	const createMut = useCustomAttributionWindowsCreate();
	const deleteMut = useCustomAttributionWindowsDelete();

	const [typeFilter, setTypeFilter] = useState<FilterType>("all");
	const [drawerOpen, setDrawerOpen] = useState(false);

	const currentPreset = listQuery.data?.preset ?? null;
	const presets = presetsQuery.data?.presets ?? [];
	const filteredPresets = useMemo(() => {
		if (typeFilter === "all") return presets;
		return presets.filter((p) => classifyPreset(p) === typeFilter);
	}, [presets, typeFilter]);

	const loading = listQuery.isLoading || presetsQuery.isLoading;
	const err = listQuery.error ?? presetsQuery.error ?? null;

	const handleApplyPreset = useCallback(
		(name: string) => {
			createMut.mutate({ preset: name });
		},
		[createMut],
	);

	const handleReset = useCallback(() => {
		deleteMut.mutate("default");
	}, [deleteMut]);

	const handleCreateCustom: SubmitHandler<CreateWindowInput> = useCallback(
		(values) => {
			createMut.mutate(
				{
					click_window_days: values.click_lookback_days,
					view_window_days: values.view_lookback_days,
				},
				{
					onSuccess: () => {
						setDrawerOpen(false);
					},
				},
			);
		},
		[createMut],
	);

	return (
		<main
			data-testid="custom-attribution-windows-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8"
		>
			{/* Topbar */}
			<Topbar
				onCreate={() => setDrawerOpen(true)}
				onReset={handleReset}
				resetBusy={deleteMut.isPending}
				currentPreset={currentPreset}
			/>

			{/* Current config summary */}
			<CurrentConfigCard
				loading={listQuery.isLoading}
				data={listQuery.data}
				error={listQuery.error?.message ?? null}
			/>

			{/* Filter row */}
			<FilterRow
				value={typeFilter}
				onChange={setTypeFilter}
				count={filteredPresets.length}
				total={presets.length}
			/>

			{/* Table of presets */}
			<PresetsTable
				loading={loading}
				error={err?.message ?? null}
				presets={filteredPresets}
				currentPreset={currentPreset}
				applyBusy={createMut.isPending}
				applyingName={
					createMut.variables && "preset" in createMut.variables
						? String(createMut.variables.preset ?? "")
						: null
				}
				onApply={handleApplyPreset}
			/>

			{createMut.error ? (
				<p
					className="text-sm text-red-600"
					role="alert"
					data-testid="custom-attribution-windows-create-error"
				>
					{createMut.error.message}
				</p>
			) : null}

			{/* Create drawer */}
			<CreateDrawer
				open={drawerOpen}
				onClose={() => setDrawerOpen(false)}
				onSubmit={handleCreateCustom}
				busy={createMut.isPending}
			/>
		</main>
	);
}

// ---------------------------------------------------------------------------
// Topbar
// ---------------------------------------------------------------------------

interface TopbarProps {
	onCreate: () => void;
	onReset: () => void;
	resetBusy: boolean;
	currentPreset: string | null;
}

function Topbar(props: TopbarProps) {
	return (
		<header
			className="flex flex-col gap-4 border-b border-zinc-200 pb-4 sm:flex-row sm:items-center sm:justify-between"
			data-testid="custom-attribution-windows-topbar"
		>
			<div className="flex flex-col gap-1">
				<p className={LABEL}>Attribution</p>
				<h1 className="text-2xl font-semibold text-zinc-900">
					Attribution Windows
				</h1>
				<p className="max-w-prose text-sm text-zinc-600">
					Configure how long a click or view is eligible to receive credit for
					a conversion. Pick a preset below, or create a custom window tailored
					to this tenant.
				</p>
			</div>
			<div className="flex flex-wrap items-center gap-2">
				{props.currentPreset && props.currentPreset !== "default" ? (
					<button
						type="button"
						className={BTN_NEUTRAL}
						onClick={props.onReset}
						disabled={props.resetBusy}
						data-testid="custom-attribution-windows-reset"
					>
						{props.resetBusy ? "Resetting…" : "Reset to default"}
					</button>
				) : null}
				<button
					type="button"
					className={BTN_PRIMARY}
					onClick={props.onCreate}
					data-testid="custom-attribution-windows-create-open"
				>
					<IconPlus />
					Create Window
				</button>
			</div>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Current config card
// ---------------------------------------------------------------------------

interface CurrentConfigCardProps {
	loading: boolean;
	data: ReturnType<typeof useCustomAttributionWindowsList>["data"];
	error: string | null;
}

function CurrentConfigCard(props: CurrentConfigCardProps) {
	if (props.loading) {
		return (
			<section
				className={`${CARD} p-5`}
				data-testid="custom-attribution-windows-current-loading"
				aria-label="Current attribution window loading"
			>
				<p className="text-sm text-zinc-500">Loading current window…</p>
			</section>
		);
	}
	if (props.error) {
		return (
			<section
				className={`${CARD} p-5`}
				data-testid="custom-attribution-windows-current-error"
				aria-label="Current attribution window error"
			>
				<p className="text-sm text-red-600" role="alert">
					{props.error}
				</p>
			</section>
		);
	}
	if (!props.data) return null;
	const data = props.data;
	return (
		<section
			className={`${CARD} grid grid-cols-1 gap-4 p-5 sm:grid-cols-4`}
			data-testid="custom-attribution-windows-current"
			aria-label="Current attribution window"
		>
			<Stat label="Active preset" value={formatPresetLabel(data.preset)} accent />
			<Stat
				label="Click lookback"
				value={formatDays(data.click_window_days)}
			/>
			<Stat
				label="View lookback"
				value={formatDays(data.view_window_days)}
			/>
			<Stat
				label="Updated"
				value={data.updated_at > 0 ? formatTimestamp(data.updated_at) : "Never"}
			/>
		</section>
	);
}

function Stat({
	label,
	value,
	accent,
}: {
	label: string;
	value: ReactNode;
	accent?: boolean;
}) {
	return (
		<div className="flex flex-col gap-1">
			<span className={LABEL}>{label}</span>
			<span
				className={
					accent
						? "text-lg font-semibold text-green-700"
						: "text-lg font-semibold text-zinc-900"
				}
			>
				{value}
			</span>
		</div>
	);
}

function formatTimestamp(sec: number): string {
	const d = new Date(sec * 1000);
	return `${d.toISOString().slice(0, 10)} ${d.toISOString().slice(11, 16)}`;
}

// ---------------------------------------------------------------------------
// Filter row
// ---------------------------------------------------------------------------

interface FilterRowProps {
	value: FilterType;
	onChange: (v: FilterType) => void;
	count: number;
	total: number;
}

function FilterRow(props: FilterRowProps) {
	return (
		<section
			className={`${CARD} flex flex-col gap-3 p-4 sm:flex-row sm:items-end sm:justify-between`}
			aria-label="Preset filters"
			data-testid="custom-attribution-windows-filters"
		>
			<div className="flex flex-col gap-1">
				<label className={LABEL} htmlFor="caw-filter-type">
					Window type
				</label>
				<select
					id="caw-filter-type"
					className={SELECT}
					value={props.value}
					onChange={(e) =>
						props.onChange(e.currentTarget.value as FilterType)
					}
					data-testid="custom-attribution-windows-type-filter"
				>
					<option value="all">All types</option>
					<option value="click">Click</option>
					<option value="impression">Impression</option>
					<option value="post-view">Post-view</option>
				</select>
			</div>
			<p className="text-xs text-zinc-500" data-testid="custom-attribution-windows-count">
				Showing <span className="font-semibold text-zinc-900">{props.count}</span> of {props.total}
			</p>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Presets table
// ---------------------------------------------------------------------------

interface PresetsTableProps {
	loading: boolean;
	error: string | null;
	presets: Preset[];
	currentPreset: string | null;
	applyBusy: boolean;
	applyingName: string | null;
	onApply: (name: string) => void;
}

function PresetsTable(props: PresetsTableProps) {
	if (props.loading) {
		return (
			<section className={`${CARD} p-6`}>
				<p
					className="text-sm text-zinc-500"
					data-testid="custom-attribution-windows-loading"
				>
					Loading attribution windows…
				</p>
			</section>
		);
	}
	if (props.error) {
		return (
			<section className={`${CARD} p-6`}>
				<p
					className="text-sm text-red-600"
					role="alert"
					data-testid="custom-attribution-windows-error"
				>
					{props.error}
				</p>
			</section>
		);
	}
	if (props.presets.length === 0) {
		return (
			<section className={`${CARD} p-10 text-center`}>
				<p
					className="text-sm text-zinc-500"
					data-testid="custom-attribution-windows-empty"
				>
					No attribution windows match the current filter.
				</p>
			</section>
		);
	}

	return (
		<section
			className={`${CARD} overflow-hidden`}
			data-testid="custom-attribution-windows-table-card"
		>
			<div className="overflow-x-auto">
				<table
					className="w-full border-collapse text-left text-sm"
					data-testid="custom-attribution-windows-list"
				>
					<thead className="bg-zinc-50">
						<tr className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
							<th scope="col" className="px-4 py-3">Name</th>
							<th scope="col" className="px-4 py-3">Type</th>
							<th scope="col" className="px-4 py-3">Window hours</th>
							<th scope="col" className="px-4 py-3">Click lookback</th>
							<th scope="col" className="px-4 py-3">View lookback</th>
							<th scope="col" className="px-4 py-3">Applied to</th>
							<th scope="col" className="px-4 py-3 text-right">
								Actions
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-zinc-200">
						{props.presets.map((p) => {
							const type = classifyPreset(p);
							const hours = daysToHours(
								Math.max(p.click_window_days, p.view_window_days),
							);
							const isApplied = props.currentPreset === p.name;
							const isApplying =
								props.applyBusy && props.applyingName === p.name;
							return (
								<tr
									key={p.name}
									className={
										isApplied
											? "bg-green-50/60"
											: "bg-white hover:bg-zinc-50"
									}
									data-testid={`custom-attribution-windows-row-${p.name}`}
								>
									<td className="px-4 py-3 font-medium text-zinc-900">
										{formatPresetLabel(p.name)}
									</td>
									<td className="px-4 py-3">
										<TypeBadge type={type} />
									</td>
									<td className="px-4 py-3 tabular-nums text-zinc-700">
										{hours.toLocaleString()}h
									</td>
									<td className="px-4 py-3 tabular-nums text-zinc-700">
										{formatDays(p.click_window_days)}
									</td>
									<td className="px-4 py-3 tabular-nums text-zinc-700">
										{formatDays(p.view_window_days)}
									</td>
									<td className="px-4 py-3">
										{isApplied ? (
											<AppliedPill />
										) : (
											<span className="text-xs text-zinc-500">
												Available
											</span>
										)}
									</td>
									<td className="px-4 py-3 text-right">
										{isApplied ? (
											<span className="text-xs text-zinc-400">
												Current
											</span>
										) : (
											<button
												type="button"
												className={BTN_LINK}
												onClick={() => props.onApply(p.name)}
												disabled={props.applyBusy}
												data-testid={`custom-attribution-windows-apply-${p.name}`}
											>
												{isApplying ? "Applying…" : "Apply preset"}
											</button>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
		</section>
	);
}

function TypeBadge({ type }: { type: WindowType }) {
	const label =
		type === "click" ? "Click" : type === "impression" ? "Impression" : "Post-view";
	return (
		<span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-2 py-0.5 text-[11px] font-medium text-zinc-700">
			{label}
		</span>
	);
}

function AppliedPill() {
	return (
		<span
			className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-semibold text-green-700"
			data-testid="custom-attribution-windows-applied-pill"
		>
			<IconCheck />
			Applied
		</span>
	);
}

// ---------------------------------------------------------------------------
// Create drawer (right-hand sheet) — zod + react-hook-form.
// ---------------------------------------------------------------------------

interface CreateDrawerProps {
	open: boolean;
	onClose: () => void;
	onSubmit: SubmitHandler<CreateWindowInput>;
	busy: boolean;
}

function CreateDrawer(props: CreateDrawerProps) {
	const {
		register,
		handleSubmit,
		formState: { errors },
		reset,
		setValue,
		watch,
	} = useForm<CreateWindowInput>({
		resolver: zodResolver(createWindowSchema),
		defaultValues: {
			name: "",
			type: "click",
			window_hours: 720,
			click_lookback_days: 30,
			view_lookback_days: 1,
		},
	});

	useEffect(() => {
		if (!props.open) reset();
	}, [props.open, reset]);

	const type = watch("type");

	// Keep window_hours in rough sync with lookback * 24 when operator changes
	// either lookback field — but do not override explicit entries on blur.
	const clickDays = watch("click_lookback_days");
	const viewDays = watch("view_lookback_days");
	useEffect(() => {
		const maxLookback = Math.max(Number(clickDays) || 0, Number(viewDays) || 0);
		if (maxLookback > 0) {
			const hours = daysToHours(maxLookback);
			setValue("window_hours", hours, { shouldDirty: false, shouldTouch: false });
		}
	}, [clickDays, viewDays, setValue]);

	if (!props.open) return null;

	return (
		<div
			className="fixed inset-0 z-40 flex"
			role="dialog"
			aria-modal="true"
			aria-label="Create attribution window"
			data-testid="custom-attribution-windows-drawer"
		>
			<button
				type="button"
				aria-label="Close drawer"
				className="flex-1 bg-zinc-900/40"
				onClick={props.onClose}
			/>
			<aside className="flex h-full w-full max-w-md flex-col bg-white shadow-2xl">
				<header className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
					<div>
						<p className={LABEL}>New</p>
						<h2 className="text-base font-semibold text-zinc-900">
							Create attribution window
						</h2>
					</div>
					<button
						type="button"
						aria-label="Close"
						className="rounded-[6px] border border-transparent p-1.5 text-zinc-500 hover:border-zinc-200 hover:bg-zinc-50"
						onClick={props.onClose}
						data-testid="custom-attribution-windows-drawer-close"
					>
						<IconClose />
					</button>
				</header>

				<form
					onSubmit={handleSubmit(props.onSubmit)}
					className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4"
					data-testid="custom-attribution-windows-create-form"
					noValidate
				>
					<Field label="Name" error={errors.name?.message} htmlFor="caw-name">
						<input
							id="caw-name"
							type="text"
							placeholder="e.g. Brand-safe 14-day"
							className={INPUT}
							data-testid="custom-attribution-windows-name-input"
							{...register("name")}
						/>
					</Field>

					<Field label="Type" error={errors.type?.message} htmlFor="caw-type">
						<select
							id="caw-type"
							className={SELECT}
							data-testid="custom-attribution-windows-type-input"
							{...register("type")}
						>
							<option value="click">Click</option>
							<option value="impression">Impression</option>
							<option value="post-view">Post-view</option>
						</select>
						<p className="mt-1 text-[11px] text-zinc-500">
							Current type: <span className="font-medium">{type}</span>
						</p>
					</Field>

					<Field
						label="Window hours"
						error={errors.window_hours?.message}
						htmlFor="caw-hours"
						hint="Total length of the attribution window (hours)."
					>
						<input
							id="caw-hours"
							type="number"
							inputMode="numeric"
							min={1}
							max={8760}
							className={INPUT}
							data-testid="custom-attribution-windows-hours-input"
							{...register("window_hours", { valueAsNumber: true })}
						/>
					</Field>

					<div className="grid grid-cols-2 gap-3">
						<Field
							label="Click lookback (days)"
							error={errors.click_lookback_days?.message}
							htmlFor="caw-click"
							hint="1–365 days"
						>
							<input
								id="caw-click"
								type="number"
								inputMode="numeric"
								min={0}
								max={365}
								className={INPUT}
								data-testid="custom-attribution-windows-click-input"
								{...register("click_lookback_days", { valueAsNumber: true })}
							/>
						</Field>
						<Field
							label="View lookback (days)"
							error={errors.view_lookback_days?.message}
							htmlFor="caw-view"
							hint="0–7 days"
						>
							<input
								id="caw-view"
								type="number"
								inputMode="numeric"
								min={0}
								max={7}
								className={INPUT}
								data-testid="custom-attribution-windows-view-input"
								{...register("view_lookback_days", { valueAsNumber: true })}
							/>
						</Field>
					</div>

					<div className="mt-auto flex items-center justify-end gap-2 border-t border-zinc-200 pt-4">
						<button
							type="button"
							className={BTN_NEUTRAL}
							onClick={props.onClose}
							disabled={props.busy}
						>
							Cancel
						</button>
						<button
							type="submit"
							className={BTN_PRIMARY}
							disabled={props.busy}
							data-testid="custom-attribution-windows-submit"
						>
							<IconPlus />
							{props.busy ? "Saving…" : "Save window"}
						</button>
					</div>
				</form>
			</aside>
		</div>
	);
}

function Field({
	label,
	error,
	htmlFor,
	hint,
	children,
}: {
	label: string;
	error?: string;
	htmlFor: string;
	hint?: string;
	children: ReactNode;
}) {
	return (
		<div className="flex flex-col gap-1">
			<label className={LABEL} htmlFor={htmlFor}>
				{label}
			</label>
			{children}
			{hint && !error ? (
				<p className="text-[11px] text-zinc-500">{hint}</p>
			) : null}
			{error ? (
				<p className="text-[11px] font-medium text-red-600" role="alert">
					{error}
				</p>
			) : null}
		</div>
	);
}

export default CustomAttributionWindowsPage;
